import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Star, Clock, CheckCircle2, XCircle, MessageSquareHeart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_COMMENT = 1000;

const Stars = ({ value, className = 'w-4 h-4' }) => (
  <span className="inline-flex items-center gap-0.5" aria-label={`${value} من 5`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} className={cn(className, n <= Math.round(value) ? 'fill-brand-gold text-brand-gold' : 'text-gray-300')} />
    ))}
  </span>
);

const StarPicker = ({ value, onChange }) => {
  const [hover, setHover] = useState(0);
  const shown = hover || value;
  return (
    <div className="inline-flex items-center gap-1" role="radiogroup" aria-label="التقييم" onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={value === n}
          aria-label={`${n} من 5`}
          onClick={() => onChange(n)}
          onMouseEnter={() => setHover(n)}
          className="p-0.5 transition-transform hover:scale-110"
        >
          <Star className={cn('w-8 h-8', n <= shown ? 'fill-brand-gold text-brand-gold' : 'text-gray-300')} />
        </button>
      ))}
    </div>
  );
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return '';
  }
};

const STATUS_NOTE = {
  pending: { icon: Clock, tone: 'bg-amber-50 text-amber-800 ring-amber-200', text: 'رأيك وصل وبانتظار موافقة الإدارة قبل ظهوره.' },
  approved: { icon: CheckCircle2, tone: 'bg-emerald-50 text-emerald-800 ring-emerald-200', text: 'رأيك منشور في صفحة المنتج، شكراً لكِ.' },
  rejected: { icon: XCircle, tone: 'bg-rose-50 text-rose-800 ring-rose-200', text: 'عذراً، لم تتم الموافقة على رأيك. تقدرين تعدّلينه وإرساله من جديد.' },
};

/**
 * آراء العملاء لمنتج: الآراء المقبولة من الإدارة فقط بتظهر للكل،
 * والكتابة متاحة للعميلة اللي استلمت المنتج فعلاً (السيرفر هو اللي بيتحقق).
 */
const ProductReviews = ({ productId }) => {
  const { token, user } = useAuth();
  const [data, setData] = useState(null);
  const [mine, setMine] = useState(null);
  const [editing, setEditing] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);

  const loadReviews = useCallback(() => {
    axios
      .get(`${API}/products/${productId}/reviews`)
      .then((res) => setData(res.data?.data))
      .catch(() => setData({ average: null, count: 0, distribution: {}, reviews: [] }));
  }, [productId]);

  const loadMine = useCallback(() => {
    if (!token) {
      setMine(null);
      return;
    }
    axios
      .get(`${API}/products/${productId}/my-review`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setMine(res.data?.data))
      .catch(() => setMine(null));
  }, [productId, token]);

  useEffect(loadReviews, [loadReviews]);
  useEffect(loadMine, [loadMine]);

  const startEditing = () => {
    setRating(mine?.review?.rating || 0);
    setComment(mine?.review?.comment || '');
    setEditing(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) {
      toast.error('اختاري التقييم بالنجوم أولاً');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/products/${productId}/reviews`,
        { rating, comment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || 'وصل رأيك، شكراً لكِ');
      setEditing(false);
      loadMine();
      loadReviews();
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data?.errors?.rating?.[0] || 'تعذّر إرسال الرأي، حاولي مرة أخرى');
    } finally {
      setSaving(false);
    }
  };

  const review = mine?.review;
  const note = review ? STATUS_NOTE[review.status] : null;
  const showForm = mine?.can_review && (!review || editing);

  return (
    <section className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1600px] py-12 border-t border-gray-100 mt-8" aria-labelledby="reviews-title">
      <div className="max-w-4xl mx-auto">
        <h2 id="reviews-title" className="font-tajawal font-extrabold text-2xl md:text-3xl text-brand-black mb-6">
          آراء العملاء
        </h2>

        {/* الملخص */}
        {data && data.count > 0 && (
          <div className="grid sm:grid-cols-[auto_1fr] gap-6 sm:gap-10 items-center rounded-2xl bg-gray-50 p-5 md:p-6 mb-8">
            <div className="text-center">
              <p className="font-tajawal font-extrabold text-5xl text-brand-black">{data.average}</p>
              <Stars value={data.average} className="w-5 h-5" />
              <p className="font-cairo text-sm text-gray-500 mt-1">{data.count} رأي</p>
            </div>
            <div className="space-y-1.5">
              {[5, 4, 3, 2, 1].map((star) => {
                const n = data.distribution?.[star] || 0;
                return (
                  <div key={star} className="flex items-center gap-3 text-sm font-cairo">
                    <span className="w-8 text-gray-600 shrink-0">{star} ★</span>
                    <div className="flex-1 h-2 rounded-full bg-gray-200 overflow-hidden">
                      <div className="h-full bg-brand-gold" style={{ width: `${(n / data.count) * 100}%` }} />
                    </div>
                    <span className="w-6 text-gray-500 text-left shrink-0">{n}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* كتابة رأي */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5 md:p-6 mb-8">
          {!token || !user ? (
            <p className="font-cairo text-gray-600">
              <Link to="/login" className="font-bold text-burgundy-500 hover:underline">سجّلي الدخول</Link> لكتابة رأيك بعد استلام المنتج.
            </p>
          ) : mine === null ? (
            <p className="font-cairo text-gray-400 text-sm">جاري التحميل...</p>
          ) : showForm ? (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <p className="font-cairo font-bold text-brand-black mb-2">شاركينا رأيك في المنتج</p>
                <StarPicker value={rating} onChange={setRating} />
              </div>
              <div>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value.slice(0, MAX_COMMENT))}
                  rows={4}
                  placeholder="اكتبي رأيك (اختياري): الخامة، المقاس، جودة التفصيل..."
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 font-cairo text-sm focus:outline-none focus:ring-2 focus:ring-burgundy-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-400 mt-1 font-cairo">{comment.length}/{MAX_COMMENT} — سيظهر رأيك بعد موافقة الإدارة.</p>
              </div>
              <div className="flex items-center gap-3">
                <Button type="submit" disabled={saving} className="bg-burgundy-500 hover:bg-burgundy-600 text-white rounded-full font-cairo font-bold px-8">
                  {saving ? 'جاري الإرسال...' : 'إرسال الرأي'}
                </Button>
                {editing && (
                  <button type="button" onClick={() => setEditing(false)} className="font-cairo text-sm text-gray-500 hover:text-gray-800">
                    إلغاء
                  </button>
                )}
              </div>
            </form>
          ) : review ? (
            <div className="space-y-3">
              <div className={cn('flex items-start gap-2 rounded-xl px-4 py-3 text-sm font-cairo ring-1', note.tone)}>
                <note.icon className="w-5 h-5 shrink-0 mt-0.5" />
                <span>{note.text}</span>
              </div>
              <div className="flex items-center gap-3">
                <Stars value={review.rating} />
                {review.comment && <p className="font-cairo text-sm text-gray-700">{review.comment}</p>}
              </div>
              {mine.can_review && (
                <button type="button" onClick={startEditing} className="font-cairo text-sm font-bold text-burgundy-500 hover:underline">
                  تعديل رأيي
                </button>
              )}
            </div>
          ) : (
            <p className="font-cairo text-gray-600 flex items-start gap-2">
              <MessageSquareHeart className="w-5 h-5 shrink-0 text-burgundy-500 mt-0.5" />
              تقدرين تكتبي رأيك في المنتج بعد طلبه واستلامه.
            </p>
          )}
        </div>

        {/* القائمة */}
        {data === null ? (
          <p className="font-cairo text-gray-400 text-sm">جاري تحميل الآراء...</p>
        ) : data.reviews.length === 0 ? (
          <p className="font-cairo text-gray-500 text-center py-6">لا توجد آراء على هذا المنتج بعد.</p>
        ) : (
          <ul className="space-y-4">
            {data.reviews.map((r) => (
              <li key={r.id} className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-full bg-burgundy-500 text-white font-tajawal font-bold flex items-center justify-center">
                      {r.name?.charAt(0)}
                    </span>
                    <div>
                      <p className="font-cairo font-bold text-brand-black leading-tight">{r.name}</p>
                      <p className="font-cairo text-xs text-gray-400">{formatDate(r.created_at)}</p>
                    </div>
                  </div>
                  <Stars value={r.rating} />
                </div>
                {r.comment && <p className="font-cairo text-gray-700 leading-relaxed whitespace-pre-line">{r.comment}</p>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

export default ProductReviews;
